/**
 * Écriture des sessions : en-tête + résumé + journal d'événements (JSONL).
 * Stockage IndexedDB (navigateur), envoi au service de télémétrie (option « partager ») par points de contrôle,
 * export manuel, et copie dans web/telemetry/ via le serveur de dev. Collecte du contexte machine.
 */
import type { TelemetryLogEntry } from '../sim/telemetry/telemetry';
import type { GameSessionHeader, GameSessionSummary, MachineInfo } from './session';

/** Session v2 (une partie). Les anciennes sessions v1 (une mission) restent lisibles par l'export. */
export interface SessionRecord {
  header: GameSessionHeader;
  summary: GameSessionSummary;
  log: TelemetryLogEntry[];
  /** État de l'envoi au serveur ; absent sur les sessions antérieures à l'envoi automatique (jamais renvoyées). */
  upload?: 'pending' | 'sent' | 'rejected';
}

const DB = 'space-arena-telemetry';
const STORE = 'sessions';
/** Service de télémétrie (web/telemetry-server), derrière le même domaine que le jeu. */
const UPLOAD_URL = import.meta.env.VITE_TELEMETRY_URL ?? (import.meta.env.DEV ? '' : `${import.meta.env.BASE_URL}api/telemetry`);
/** Au-delà, `keepalive` est refusé par le navigateur : la session part au point de contrôle ou au lancement suivant. */
const KEEPALIVE_MAX = 60 * 1024;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'header.sessionId' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function toJsonl(record: { header: object; summary: object; log: object[] }): string {
  const lines = [
    JSON.stringify({ kind: 'header', ...record.header }),
    JSON.stringify({ kind: 'summary', ...record.summary }),
    ...record.log.map((entry) => JSON.stringify({ kind: 'event', ...entry })),
  ];
  return lines.join('\n') + '\n';
}

async function put(record: SessionRecord): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // IndexedDB indisponible : la session reste exportable depuis l'écran de résultats.
  }
}

/** Envoie une session ; renvoie l'état à mémoriser (« pending » = réessayer plus tard). */
async function upload(record: SessionRecord): Promise<NonNullable<SessionRecord['upload']>> {
  const { header, summary, log } = record;
  const body = JSON.stringify({ header, summary, log });
  try {
    const response = await fetch(UPLOAD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: body.length < KEEPALIVE_MAX,
    });
    if (response.ok) return 'sent';
    // 4xx hors limite de débit : le serveur refuse ce contenu, inutile d'insister.
    return response.status >= 400 && response.status < 500 && response.status !== 429 ? 'rejected' : 'pending';
  } catch {
    return 'pending';
  }
}

/** Envois en cours par session : un point de contrôle part après le précédent (le serveur garde le dernier reçu). */
const chains = new Map<string, Promise<void>>();

/**
 * Enregistre la session dans le navigateur puis, si le joueur l'accepte, l'envoie au serveur. Appelé à chaque
 * point de contrôle : la même session est réécrite (IndexedDB, serveur, copie de dev).
 */
export function saveSession(record: SessionRecord, share: boolean): Promise<void> {
  const id = record.header.sessionId;
  const send = share && UPLOAD_URL !== '';
  record.upload = send ? 'pending' : undefined;
  const run = async () => {
    await put(record);
    if (send) {
      record.upload = await upload(record);
      if (record.upload !== 'pending') await put(record);
    }
    if (import.meta.env.DEV) {
      const who = record.header.player.replace(/[^\w-]/g, '_') || 'pilote';
      const name = `${record.header.startedAt.replace(/[:.]/g, '-')}_${who}.jsonl`;
      await fetch('/__dev/telemetry', { method: 'POST', body: JSON.stringify({ name, content: toJsonl(record) }) }).catch(() => undefined);
    }
  };
  const next = (chains.get(id) ?? Promise.resolve()).then(run, run);
  chains.set(id, next);
  return next;
}

/** Renvoie les sessions restées en attente (fermeture de l'onglet, hors ligne, serveur indisponible). */
export async function flushPending(): Promise<void> {
  if (UPLOAD_URL === '') return;
  for (const record of await listSessions()) {
    if (record.upload !== 'pending') continue;
    record.upload = await upload(record);
    if (record.upload === 'pending') return; // serveur toujours injoignable : on réessaiera plus tard
    await put(record);
  }
}

export async function listSessions(): Promise<SessionRecord[]> {
  try {
    const db = await openDb();
    const all = await new Promise<SessionRecord[]>((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      request.onsuccess = () => resolve(request.result as SessionRecord[]);
      request.onerror = () => reject(request.error);
    });
    db.close();
    return all;
  } catch {
    return [];
  }
}

export function download(name: string, content: string): void {
  const blob = new Blob([content], { type: 'application/x-ndjson' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function webglInfo(canvas: HTMLCanvasElement): { vendor: string; renderer: string; version: number; maxTextureSize: number } {
  try {
    const gl2 = canvas.getContext('webgl2');
    const gl = gl2 ?? canvas.getContext('webgl');
    if (!gl) return { vendor: 'inconnu', renderer: 'inconnu', version: 0, maxTextureSize: 0 };
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      vendor: String(ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR)),
      renderer: String(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)),
      version: gl2 ? 2 : 1,
      maxTextureSize: Number(gl.getParameter(gl.MAX_TEXTURE_SIZE)),
    };
  } catch {
    return { vendor: 'inconnu', renderer: 'inconnu', version: 0, maxTextureSize: 0 };
  }
}

/** Contexte machine rapporté par le navigateur (rapports de bug, performances par matériel). */
export function machineInfo(canvas: HTMLCanvasElement, engine: string): MachineInfo {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    userAgentData?: { brands?: { brand: string; version: string }[]; platform?: string; mobile?: boolean };
  };
  const gl = webglInfo(canvas);
  return {
    userAgent: nav.userAgent,
    platform: nav.userAgentData?.platform || nav.platform || 'inconnu',
    brands: (nav.userAgentData?.brands ?? []).map((b) => `${b.brand} ${b.version}`).join(', '),
    mobile: nav.userAgentData?.mobile ?? /Mobi|Android/i.test(nav.userAgent),
    language: nav.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cpuCores: nav.hardwareConcurrency ?? null,
    memoryGb: nav.deviceMemory ?? null,
    screen: { width: screen.width, height: screen.height, colorDepth: screen.colorDepth },
    devicePixelRatio: window.devicePixelRatio,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    touchPoints: nav.maxTouchPoints ?? 0,
    gpu: { vendor: gl.vendor, renderer: gl.renderer },
    webgl: { version: gl.version, maxTextureSize: gl.maxTextureSize },
    engine,
  };
}

/** Statistiques d'images par seconde sur la session. */
export class FpsMeter {
  private samples: number[] = [];
  private last = 0;
  add(dt: number): void {
    if (dt > 0 && dt < 1) {
      this.last = 1 / dt;
      this.samples.push(this.last);
    }
    if (this.samples.length > 20000) this.samples.splice(0, 10000);
  }
  /** Dernière mesure (échantillons de vol). */
  current(): number { return this.last; }
  stats(): GameSessionHeader['fps'] {
    if (this.samples.length === 0) return { average: 0, p05: 0, samples: 0 };
    const sorted = [...this.samples].sort((a, b) => a - b);
    const avg = sorted.reduce((a, b) => a + b, 0) / sorted.length;
    return { average: Math.round(avg * 10) / 10, p05: Math.round(sorted[Math.floor(sorted.length * 0.05)]! * 10) / 10, samples: sorted.length };
  }
}
